// Aba "Fluxo padrão" do Flowbuilder (/flow-default) — mockup "Fluxo Padrão
// A": três gatilhos automáticos (boas-vindas, resposta padrão, inatividade)
// com select de fluxo de 200px e botão Salvar no cabeçalho.
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import { AccountTree, WavingHand, Reply, HourglassEmpty } from "@mui/icons-material";

import MainContainer from "../../components/MainContainer";
import FlowBuilderNav from "../../components/FlowBuilderNav";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import useFlowPageStyles from "../FlowBuilder/flowPageStyles";
import { TONES } from "../FlowBuilderConfig/flowRules";

// Campos do GET (flowIdNotPhrase) e do POST/PUT (flowIdPhrase) divergem no
// backend — mantidos como estão.
const ITEMS = [
  { key: "welcome", readField: "flowIdWelcome", writeField: "flowIdWelcome", icon: WavingHand, tone: "act" },
  { key: "reply", readField: "flowIdNotPhrase", writeField: "flowIdPhrase", icon: Reply, tone: "msg" },
  { key: "inactive", readField: "flowIdInactiveTime", writeField: "flowIdInactiveTime", icon: HourglassEmpty, tone: "ai" },
];

const FlowDefault = () => {
  const classes = useFlowPageStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flows, setFlows] = useState([]);
  const [exists, setExists] = useState(false);
  const [values, setValues] = useState({ welcome: "", reply: "", inactive: "" });

  useEffect(() => {
    const load = async () => {
      try {
        const [f, d] = await Promise.all([api.get("/flowbuilder"), api.get("/flowdefault")]);
        const list = f.data?.flows || [];
        setFlows(list);
        const cfg = d.data?.flow || {};
        setExists(!!cfg.companyId);
        const pick = (id) => (list.some((x) => x.id === id) ? id : "");
        setValues({
          welcome: pick(cfg.flowIdWelcome),
          reply: pick(cfg.flowIdNotPhrase),
          inactive: pick(cfg.flowIdInactiveTime),
        });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      ITEMS.forEach((it) => {
        payload[it.writeField] = values[it.key] || null;
      });
      await (exists ? api.put("/flowdefault", payload) : api.post("/flowdefault", payload));
      setExists(true);
      toast.success(i18n.t("aeros.flow.defaults.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainContainer>
      <div className={classes.pageHeader}>
        <FlowBuilderNav />
      </div>
      <div className={classes.pageBody}>
        <div className={classes.table}>
          <div className={classes.sectionHead}>
            <div>
              <div className={classes.sectionTitle}>{i18n.t("aeros.flow.defaults.title")}</div>
              <div className={classes.sectionSub}>{i18n.t("aeros.flow.defaults.subtitle")}</div>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={classes.gradientButton}
              style={{ marginLeft: "auto", padding: "7px 14px", fontSize: 12.5 }}
              onClick={handleSave}
              disabled={saving || loading}
            >
              {i18n.t("aeros.flow.save")}
            </Button>
          </div>
          <div style={{ padding: "16px 24px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
            {loading ? (
              <div className={classes.loading}><CircularProgress color="primary" /></div>
            ) : (
              ITEMS.map((it) => {
                const Icon = it.icon;
                const t = TONES[it.tone];
                const value = values[it.key];
                return (
                  <div key={it.key} className={classes.defaultRow}>
                    <div className={classes.defaultTile} style={{ background: t.bg, color: t.color }}>
                      <Icon />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={classes.defaultTitle}>{i18n.t(`aeros.flow.defaults.${it.key}`)}</div>
                      <div className={classes.defaultDesc}>{i18n.t(`aeros.flow.defaults.${it.key}Desc`)}</div>
                    </div>
                    <TextField
                      select
                      variant="outlined"
                      className={classes.defaultSelect}
                      value={value}
                      onChange={(e) => { const valor = e.target.value; setValues((old) => ({ ...old, [it.key]: valor })); }}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (v) => {
                          const f = flows.find((x) => x.id === v);
                          return (
                            <>
                              <AccountTree className={classes.flowIcon} style={{ fontSize: 15 }} />
                              <span className={`${classes.ellipsis} ${f ? "" : classes.muted}`} style={{ flex: 1 }}>
                                {f ? f.name : i18n.t("aeros.flow.defaults.none")}
                              </span>
                            </>
                          );
                        },
                      }}
                      inputProps={{ "aria-label": i18n.t(`aeros.flow.defaults.${it.key}`) }}
                    >
                      <MenuItem value="" style={{ fontSize: 13 }}>{i18n.t("aeros.flow.defaults.none")}</MenuItem>
                      {flows.map((f) => (
                        <MenuItem key={f.id} value={f.id} style={{ fontSize: 13 }}>{f.name}</MenuItem>
                      ))}
                    </TextField>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default FlowDefault;
